import { useParams, Link } from "react-router-dom";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/profile";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <title>文章未找到 - {siteInfo.name}</title>
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.id === article.category);
  const relatedArticles = slug ? getRelatedArticles(slug, article.category, 3) : [];

  const pageTitle = `${article.title} - ${siteInfo.name}`;
  const pageDesc = article.excerpt.replace(/`|「|」/g, "");
  const articleUrl = `${siteInfo.url}/article/${article.slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: pageDesc,
    image: article.coverImage,
    datePublished: article.date,
    author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: siteInfo.name },
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
  };

  const pageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: articleUrl,
    name: article.title,
    description: pageDesc,
    isPartOf: { "@id": `${siteInfo.url}/#website` },
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${article.tags.join(",")},${siteInfo.keywords}`} />
      <link rel="canonical" href={articleUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={article.coverImage} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:author" content={article.author} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={article.coverImage} />
      <script type="application/ld+json">
        {JSON.stringify(articleJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(pageJsonLd)}
      </script>

      <div className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: categoryInfo?.name || article.category, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs bg-primary-100 text-primary-600 px-3 py-1 rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  {categoryInfo?.name || article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i> {article.likes.toLocaleString()} 赞
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed mb-10 [&_p]:mb-4 [&_p]:text-foreground-700 [&_strong]:text-foreground-950 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6 border-t border-background-200">
              <div className="flex items-center gap-3">
                <button
                  className="flex items-center gap-1.5 px-4 py-2 bg-background-100 rounded-full text-sm text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  <i className="ri-arrow-up-line"></i> 回到顶部
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors">
                  <i className="ri-twitter-x-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-10">
                <h3 className="text-xl font-semibold text-foreground-950 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100/80 transition-all duration-300"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </>
  );
}